import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  BenchmarkType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { HabitatConnectivityExplainer } from '@/components/NCExplainers/HabitatConnectivityExplainer';
import { COMPLIANCE_LOGOS_HABITAT_CONNECTIVITY } from '@/config/constants';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { HabitatConnectivityGraphTile } from './GraphTile';
import { HabitatConnectivityStatsTile } from './StatsTile';

export const HabitatConnectivity = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.habitatConnectivity.labels.title')}>
      <HabitatConnectivityCard />
    </PlotCardWrapper>
  );
};

const HabitatConnectivityCard = () => {
  const { t } = useTranslation();

  const purchasedIndicatorProducts = useSelectedIndicatorProducts();
  const hasIPSubscription = purchasedIndicatorProducts.some((ip) => ip.name === INDICATOR.STRUCTURAL_INTEGRITY);

  const factNames = {
    latestAnalysis: IndicatorName.structural_integrity,
    benchmark: IndicatorName.structural_integrity_benchmark,
  };

  const facts = usePlotFacts(Object.values(factNames));

  if (!hasIPSubscription) {
    return null;
  }

  const latestAnalysisFact = getFactFromFactArray<number>(facts, factNames.latestAnalysis);
  const veryLowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.very_low,
  });
  const lowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });
  const moderateBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const highBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const veryHighBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.very_high,
  });

  const graphBenchmarks = getBenchmarksFromFacts({
    veryLowBenchmarkFact,
    lowBenchmarkFact,
    moderateBenchmarkFact,
    highBenchmarkFact,
    veryHighBenchmarkFact,
  });

  const hasData = !isNullish(latestAnalysisFact?.value);

  if (!hasData) {
    return null;
  }

  const latestAnalysisFactAsPercentage = {
    ...latestAnalysisFact,
    value: !isNullish(latestAnalysisFact?.value) ? (latestAnalysisFact?.value ?? 0) * 100 : latestAnalysisFact?.value, // structural_integrity is an EII component which range [0,1], but this card shows percentages
  } as PlotReportFactElement<number | null> | undefined;

  const benchmarksAsPercentage = graphBenchmarks.map(
    (benchmark) => (typeof benchmark === 'number' ? benchmark * 100 : benchmark), // structural_integrity is an EII component which range [0,1], but this card shows percentages
  );

  return (
    <NCCard explainer={<HabitatConnectivityExplainer />} data-testid='habitat-connectivity-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.habitatConnectivity.labels.title')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <HabitatConnectivityGraphTile
          latestAnalysisFact={latestAnalysisFactAsPercentage}
          benchmarks={benchmarksAsPercentage}
        />
        <HabitatConnectivityStatsTile
          latestAnalysisFact={latestAnalysisFactAsPercentage}
          benchmarks={benchmarksAsPercentage}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_HABITAT_CONNECTIVITY} />
    </NCCard>
  );
};
